import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Upload,
  Trash2,
  FileText,
  Image,
  File,
  Download,
  Eye,
  RefreshCw,
  FolderOpen,
  Folder,
  FolderPlus,
  AlertCircle,
  Loader2,
  ChevronRight,
  Home,
  Link2,
  Unlink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  createdTime?: string;
  modifiedTime?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;
const FOLDER_MIME = "application/vnd.google-apps.folder";

function hasDraggedFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer?.types) return false;
  return Array.from(dataTransfer.types).includes("Files");
}

function getFileIcon(mimeType: string) {
  if (mimeType === FOLDER_MIME) return <Folder className="w-5 h-5 text-primary" />;
  if (mimeType.startsWith("image/")) return <Image className="w-5 h-5 text-primary" />;
  if (mimeType === "application/pdf") return <FileText className="w-5 h-5 text-destructive" />;
  return <File className="w-5 h-5 text-muted-foreground" />;
}

function formatFileSize(bytes?: string): string {
  if (!bytes) return "—";
  const size = parseInt(bytes, 10);
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isPreviewable(mimeType: string): boolean {
  return mimeType.startsWith("image/") || mimeType === "application/pdf";
}

export function FileLibrarySection() {
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<DriveFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const authWindowRef = useRef<Window | null>(null);
  // Navigation par dossiers (arborescence)
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { id: "root", name: "Racine" },
  ]);

  const currentFolderId = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 1].id : undefined;

  const getAuthHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Session expirée");
    return {
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    };
  }, []);

  // Check Google Drive connection status
  const checkConnection = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=check-connection`,
        { headers }
      );
      const data = await resp.json();
      setConnected(data.connected === true);
      return data.connected === true;
    } catch {
      setConnected(false);
      return false;
    }
  }, [getAuthHeaders]);

  // Start Google OAuth flow
  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const headers = await getAuthHeaders();
      const redirectUri = `${window.location.origin}/gdrive-callback`;
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=auth-url&redirect_uri=${encodeURIComponent(redirectUri)}`,
        { headers }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      // Open Google consent in a popup
      const popup = window.open(data.url, "gdrive-auth", "width=600,height=700,left=200,top=100");
      authWindowRef.current = popup;

      // Listen for the callback message
      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type !== "gdrive-callback") return;
        window.removeEventListener("message", handleMessage);

        const { code } = event.data;
        if (!code) {
          toast({ title: "Erreur", description: "Aucun code d'autorisation reçu.", variant: "destructive" });
          setConnecting(false);
          return;
        }

        try {
          const callbackHeaders = await getAuthHeaders();
          const callbackResp = await fetch(
            `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=auth-callback`,
            {
              method: "POST",
              headers: { ...callbackHeaders, "Content-Type": "application/json" },
              body: JSON.stringify({ code, redirect_uri: redirectUri }),
            }
          );
          const callbackData = await callbackResp.json();
          if (!callbackResp.ok) throw new Error(callbackData.error);

          setConnected(true);
          toast({ title: "Google Drive connecté", description: "Votre bibliothèque de fichiers est prête." });
          fetchFiles();
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Erreur inconnue";
          toast({ title: "Erreur de connexion", description: msg, variant: "destructive" });
        } finally {
          setConnecting(false);
        }
      };

      window.addEventListener("message", handleMessage);

      // Fallback: poll for popup close
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setTimeout(() => {
            setConnecting(false);
            checkConnection();
          }, 1000);
        }
      }, 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
      setConnecting(false);
    }
  }, [getAuthHeaders, checkConnection]);

  // Disconnect Google Drive
  const handleDisconnect = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=disconnect`,
        { method: "POST", headers }
      );
      setConnected(false);
      setFiles([]);
      toast({ title: "Google Drive déconnecté" });
    } catch {
      toast({ title: "Erreur", description: "Impossible de déconnecter.", variant: "destructive" });
    }
  }, [getAuthHeaders]);

  const fetchFiles = useCallback(async (parentId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      let url = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=list`;
      if (parentId) url += `&parentId=${parentId}`;
      const resp = await fetch(url, { headers });
      const data = await resp.json();
      if (!resp.ok) {
        if (data.error === "GDRIVE_NOT_CONNECTED" || data.error === "GDRIVE_TOKEN_EXPIRED") {
          setConnected(false);
          return;
        }
        throw new Error(data.error || "Erreur lors du chargement");
      }
      // Séparer dossiers en premier, puis fichiers
      const sorted = (data.files || []).sort((a: DriveFile, b: DriveFile) => {
        const aIsFolder = a.mimeType === FOLDER_MIME ? 0 : 1;
        const bIsFolder = b.mimeType === FOLDER_MIME ? 0 : 1;
        if (aIsFolder !== bIsFolder) return aIsFolder - bIsFolder;
        return a.name.localeCompare(b.name, "fr");
      });
      setFiles(sorted);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkConnection().then((isConnected) => {
      if (isConnected) fetchFiles(currentFolderId);
      else setLoading(false);
    });
  }, [currentFolderId, checkConnection]);

  useEffect(() => {
    const preventBrowserFileOpen = (event: DragEvent) => {
      if (!hasDraggedFiles(event.dataTransfer)) return;
      event.preventDefault();
    };

    const resetDragState = () => {
      dragCounterRef.current = 0;
      setIsDragging(false);
    };

    window.addEventListener("dragover", preventBrowserFileOpen);
    window.addEventListener("drop", preventBrowserFileOpen);
    window.addEventListener("drop", resetDragState);
    window.addEventListener("dragend", resetDragState);

    return () => {
      window.removeEventListener("dragover", preventBrowserFileOpen);
      window.removeEventListener("drop", preventBrowserFileOpen);
      window.removeEventListener("drop", resetDragState);
      window.removeEventListener("dragend", resetDragState);
    };
  }, []);

  const navigateToFolder = (folder: DriveFile) => {
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const navigateToBreadcrumb = (index: number) => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
  };

  const uploadFile = useCallback(async (file: File) => {
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: "Fichier trop volumineux", description: `"${file.name}" dépasse 20 Mo.`, variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append("file", file);
      if (currentFolderId) formData.append("parentId", currentFolderId);
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=upload`,
        { method: "POST", headers, body: formData }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Erreur lors de l'upload");
      toast({ title: "Fichier envoyé", description: `"${file.name}" a été ajouté.` });
      await fetchFiles(currentFolderId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      toast({ title: "Erreur d'upload", description: msg, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [getAuthHeaders, currentFolderId, fetchFiles]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    e.target.value = "";
  };


  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!hasDraggedFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!hasDraggedFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!hasDraggedFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    if (!hasDraggedFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files ?? []);
    if (droppedFiles.length === 0) return;

    for (const file of droppedFiles) {
      await uploadFile(file);
    }
  }, [uploadFile]);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      const headers = await getAuthHeaders();
      const body: Record<string, string> = { name };
      if (currentFolderId) body.parentId = currentFolderId;
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=create-folder`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Erreur lors de la création");
      toast({ title: "Dossier créé", description: `"${name}" a été créé.` });
      setNewFolderName("");
      setShowNewFolderInput(false);
      await fetchFiles(currentFolderId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDelete = async (fileId: string, fileName: string) => {
    setDeletingId(fileId);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=delete&fileId=${fileId}`,
        { method: "DELETE", headers }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Erreur lors de la suppression");
      toast({ title: "Fichier supprimé", description: `"${fileName}" a été supprimé.` });
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const handlePreview = async (file: DriveFile) => {
    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=get&fileId=${file.id}`,
        { headers }
      );
      if (!resp.ok) throw new Error("Impossible de charger le fichier");
      const blob = await resp.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      toast({ title: "Erreur d'aperçu", description: "Impossible de charger le fichier.", variant: "destructive" });
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (file: DriveFile) => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdrive-files?action=get&fileId=${file.id}`,
        { headers }
      );
      if (!resp.ok) throw new Error("Impossible de télécharger");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Erreur", description: "Impossible de télécharger le fichier.", variant: "destructive" });
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const folders = files.filter((f) => f.mimeType === FOLDER_MIME);
  const regularFiles = files.filter((f) => f.mimeType !== FOLDER_MIME);

  return (
    <section className="w-full max-w-6xl mx-auto mt-12 sm:mt-16">
      <Card
        className={`border-border/50 bg-card/80 backdrop-blur-sm relative ${isDragging && connected ? "ring-2 ring-primary ring-offset-2" : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drop overlay — covers entire card */}
        {isDragging && connected && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-lg backdrop-blur-sm pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-primary pointer-events-none">
              <Upload className="w-10 h-10" />
              <p className="text-sm font-medium">Déposez vos fichiers ici</p>
            </div>
          </div>
        )}
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg sm:text-xl font-cinzel text-primary">
                Bibliothèque de fichiers
              </CardTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Vos documents privés sur Google Drive
              </p>
            </div>
          </div>
          {connected === false ? (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button size="sm" onClick={handleConnect} disabled={connecting}>
                {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                {connecting ? "Connexion..." : "Connecter Google Drive"}
              </Button>
            </div>
          ) : connected === true ? (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={() => fetchFiles(currentFolderId)} disabled={loading} className="flex-shrink-0">
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                Actualiser
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowNewFolderInput(true)} className="flex-shrink-0">
                <FolderPlus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Nouveau dossier</span>
              </Button>
              <label className="flex-1 sm:flex-none">
                <Input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv" />
                <Button asChild size="sm" disabled={uploading} className="w-full sm:w-auto cursor-pointer">
                  <span>
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    {uploading ? "Envoi..." : "Envoyer"}
                  </span>
                </Button>
              </label>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Déconnecter Google Drive">
                    <Unlink className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Déconnecter Google Drive ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Vos fichiers resteront sur Google Drive mais vous ne pourrez plus y accéder depuis cette interface.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDisconnect}>Déconnecter</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : null}
        </CardHeader>

        <CardContent>
          {/* Not connected state */}
          {connected === false && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Link2 className="w-12 h-12 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                Connectez votre compte Google Drive pour gérer vos fichiers
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Vos documents seront stockés sur votre Google Drive personnel
              </p>
              <Button onClick={handleConnect} disabled={connecting}>
                {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                {connecting ? "Connexion en cours..." : "Connecter Google Drive"}
              </Button>
            </div>
          )}

          {/* Breadcrumb / fil d'Ariane */}
          {connected && breadcrumb.length > 1 && (
            <nav className="flex items-center gap-1 mb-4 p-2 rounded-lg bg-muted/40 border border-border/30 flex-wrap text-sm">
              {breadcrumb.map((item, i) => (
                <React.Fragment key={item.id}>
                  {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                  <button
                    onClick={() => navigateToBreadcrumb(i)}
                    className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/10 transition-colors ${
                      i === breadcrumb.length - 1
                        ? "text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {i === 0 && <Home className="w-3.5 h-3.5" />}
                    <span className="truncate max-w-[120px]">{item.name}</span>
                  </button>
                </React.Fragment>
              ))}
            </nav>
          )}

          {/* New folder input */}
          {connected && showNewFolderInput && (
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-muted/40 border border-border/30">
              <FolderPlus className="w-5 h-5 text-primary flex-shrink-0" />
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Nom du nouveau dossier..."
                className="flex-1 h-8 text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                autoFocus
              />
              <Button size="sm" onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
                {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); }}>
                Annuler
              </Button>
            </div>
          )}

          {/* Error */}
          {connected && error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-4">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive flex-1">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => fetchFiles(currentFolderId)}>Réessayer</Button>
            </div>
          )}

          {/* Loading */}
          {connected && loading && !error && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border/30">
                  <Skeleton className="w-10 h-10 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty */}
          {connected && !loading && !error && files.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FolderOpen className="w-12 h-12 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                {breadcrumb.length > 1 ? "Ce dossier est vide" : "Aucun fichier dans votre bibliothèque"}
              </p>
              <p className="text-xs text-muted-foreground">
                Envoyez vos premiers documents (PDF, images, etc.)
              </p>
            </div>
          )}

          {/* Folders */}
          {connected && !loading && folders.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-1">
                Dossiers
              </p>
              <div className="flex flex-col gap-1.5">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => navigateToFolder(folder)}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border/30 hover:border-secondary/40 hover:bg-secondary/5 transition-colors text-left group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Folder className="w-5 h-5 text-secondary" />
                    </div>
                    <span className="text-sm font-medium text-foreground truncate group-hover:text-secondary transition-colors">
                      {folder.name}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {connected && !loading && regularFiles.length > 0 && (
            <div>
              {folders.length > 0 && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Fichiers
                </p>
              )}
              <div className="space-y-1.5">
                {regularFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border/30 hover:border-primary/20 hover:bg-muted/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded bg-muted/50 flex items-center justify-center flex-shrink-0">
                      {getFileIcon(file.mimeType)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {file.mimeType.split("/").pop()?.toUpperCase()}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">{formatFileSize(file.size)}</span>
                        <span className="text-[11px] text-muted-foreground hidden sm:inline">• {formatDate(file.modifiedTime)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {isPreviewable(file.mimeType) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(file)} title="Aperçu">
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(file)} title="Télécharger">
                        <Download className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" disabled={deletingId === file.id} title="Supprimer">
                            {deletingId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce fichier ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Le fichier "{file.name}" sera définitivement supprimé. Cette action est irréversible.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(file.id, file.name)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {connected && <p className="text-[11px] text-muted-foreground mt-4 text-center">
            Formats acceptés : PDF, images (JPG, PNG, WebP), documents Office, texte — Taille max : 20 Mo
          </p>}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-cinzel text-primary truncate pr-8">{previewFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto flex items-center justify-center min-h-[300px]">
            {previewLoading && (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Chargement de l'aperçu...</p>
              </div>
            )}
            {previewUrl && previewFile?.mimeType.startsWith("image/") && (
              <img src={previewUrl} alt={previewFile.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
            )}
            {previewUrl && previewFile?.mimeType === "application/pdf" && (
              <iframe src={previewUrl} className="w-full h-[70vh] rounded-lg border" title={previewFile.name} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
